import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button, Badge } from "@tabipla/ui";
import { ConfirmDialog } from "@tabipla/components";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";
import type { SessionResponseDto, GroupMemberResponseDto } from "@tabipla/shared";
import {
  useExpenses,
  useBalances,
  useCreateExpense,
  useDeleteExpense,
} from "../../../application/hooks/useExpenses";
import { useCompleteSession, useReopenSession } from "../../../application/hooks/useSessions";
import { BalanceSummary } from "./BalanceSummary";
import { SettlementView } from "./SettlementView";
import { ExpenseList } from "./ExpenseList";
import { ExpenseForm } from "./ExpenseForm";

interface SessionDetailProps {
  groupId: string;
  session: SessionResponseDto;
  members: GroupMemberResponseDto[];
  currentUserId: string;
  currentUserType: "user" | "guest";
  onBack: () => void;
  canEdit: boolean;
}

export function SessionDetail({
  groupId,
  session,
  members,
  currentUserId,
  currentUserType,
  onBack,
  canEdit,
}: SessionDetailProps) {
  const { t } = useTranslation();
  const { confirm, dialogProps } = useConfirmDialog();
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);

  const { data: expenses } = useExpenses(groupId, session.id);
  const { data: balances } = useBalances(groupId, session.id);

  const createExpenseMutation = useCreateExpense(groupId, session.id);
  const deleteExpenseMutation = useDeleteExpense(groupId, session.id);
  const completeMutation = useCompleteSession(groupId);
  const reopenMutation = useReopenSession(groupId);

  const isCompleted = session.status === "completed";
  const canModify = canEdit && !isCompleted;

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  const handleComplete = async () => {
    try {
      await completeMutation.mutateAsync(session.id);
      toast.success(t("sessions.completeSuccess", "精算を完了しました"));
    } catch {
      toast.error(t("sessions.completeError", "完了に失敗しました"));
    }
  };

  const handleReopen = async () => {
    try {
      await reopenMutation.mutateAsync(session.id);
      toast.success(t("sessions.reopenSuccess", "精算を再開しました"));
    } catch {
      toast.error(t("sessions.reopenError", "再開に失敗しました"));
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("sessions.backToList", "精算一覧に戻る")}
        </button>
      </div>

      {/* Session Info */}
      <div className="flex items-start justify-between rounded-lg border bg-card p-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold">{session.name}</h2>
            <Badge variant={isCompleted ? "secondary" : "default"} className="text-xs">
              {isCompleted ? t("sessions.completed", "完了") : t("sessions.active", "進行中")}
            </Badge>
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
            <span>
              {t("sessions.totalExpenses", "合計出費")}:{" "}
              <span className="font-semibold text-foreground">
                {formatAmount(session.totalExpenses)}
              </span>
            </span>
            <span>·</span>
            <span>
              {session.expenseCount}
              {t("sessions.expenseCount", "件")}
            </span>
          </div>
        </div>
        <div>
          {canEdit && !isCompleted && (
            <Button
              variant="outline"
              size="sm"
              className="text-green-600 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-950"
              onClick={handleComplete}
              disabled={completeMutation.isPending}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              {t("sessions.complete", "精算完了")}
            </Button>
          )}
          {canEdit && isCompleted && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReopen}
              disabled={reopenMutation.isPending}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {t("sessions.reopen", "再開")}
            </Button>
          )}
        </div>
      </div>

      {/* Completed Session Warning */}
      {isCompleted && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
          {t(
            "sessions.completedWarning",
            "この精算は完了しています。編集するには「再開」をクリックしてください。"
          )}
        </div>
      )}

      {/* Balance Summary */}
      {balances && balances.length > 0 && <BalanceSummary balances={balances} />}

      {/* Settlements */}
      <SettlementView
        groupId={groupId}
        sessionId={session.id}
        currentUserId={currentUserId}
        currentUserType={currentUserType}
        canEdit={canModify}
      />

      {/* Expense List */}
      <ExpenseList
        expenses={expenses ?? []}
        onAddClick={() => setShowExpenseDialog(true)}
        onDeleteClick={async (expenseId: string) => {
          const confirmed = await confirm({
            title: t("expenses.deleteTitle"),
            description: t("expenses.deleteConfirm"),
            confirmText: t("common.delete"),
            cancelText: t("common.cancel"),
            destructive: true,
          });
          if (confirmed) {
            deleteExpenseMutation.mutate(expenseId);
          }
        }}
        canEdit={canModify}
      />

      {/* Expense Form Dialog */}
      <ExpenseForm
        open={showExpenseDialog}
        onOpenChange={setShowExpenseDialog}
        members={members}
        currentUserId={currentUserId}
        sessionId={session.id}
        onSubmit={(expense) => {
          createExpenseMutation.mutate(expense, {
            onSuccess: () => setShowExpenseDialog(false),
          });
        }}
        isSubmitting={createExpenseMutation.isPending}
      />

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
